// src/ProjectDialog.js
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import RoomIcon from '@mui/icons-material/Room';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { useSnackbar } from 'notistack';
import api from './apiClient';

function ProjectDialog({
  open,
  onClose,
  workspaceId,
  project,
  currentUserId,   // ✅ comes from DashboardView
  onSave,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(project && project.id);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // When dialog opens / project changes, populate fields
  useEffect(() => {
    if (open && project) {
      setName(project.name || '');
      setDescription(project.description || '');
    } else if (open) {
      setName('');
      setDescription('');
    }
  }, [open, project]);

 const handleSave = async () => {
  try {
    if (!name.trim()) {
      enqueueSnackbar('Project name is required', { variant: 'warning' });
      return;
    }

    if (!isEdit && !workspaceId) {
      enqueueSnackbar('No workspace selected for this project', {
        variant: 'error',
      });
      return;
    }

    const basePayload = {
      name: name.trim(),
      description: (description || '').trim(),
      owner_id: currentUserId,
      user_id: currentUserId,  // Added for authentication, as seen in other API calls
    };

    if (isEdit) {
      // --- UPDATE existing project ---
      console.log('Updating project with payload:', basePayload);
      await api.put(`/api/projects/${project.id}`, basePayload);
    } else {
      // --- CREATE new project ---
      const createPayload = {
        ...basePayload,
        workplace_id: workspaceId,  // Use 'workplace_id' based on backend field (remove duplicate 'workspace_id')
      };

      console.log('Creating project with payload:', createPayload);

      await api.post('/api/projects', createPayload);
    }

    enqueueSnackbar(isEdit ? 'Project updated' : 'Project created', {
      variant: 'success',
    });

    onSave && onSave();
    onClose && onClose();
  } catch (err) {
    const backendData = err?.response?.data;
    console.error('Error saving project', err);
    console.error(
      'Backend response (stringified):',
      backendData ? JSON.stringify(backendData) : '(no data)'
    );
    enqueueSnackbar(backendData?.message || 'Error saving project', { variant: 'error' });
  }
};


  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit Project' : 'New Project'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {/* Little icon row like in your screenshot – just visual for now */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              mt: 1,
            }}
          >
            <Tooltip title="Location (future feature)">
              <span>
                <IconButton size="small" disabled>
                  <RoomIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Google link (future feature)">
              <span>
                <IconButton size="small" disabled>
                  <GpsFixedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProjectDialog;
